/**
 * PasswordRecovery (Authentication) — forgot-password page rebuilt on
 * composition primitives.
 *
 * Layout tree:
 *   AuthShell
 *     CenteredCard (cardAs=form, radius=xl, paddingX=m, paddingY=m+xl asym,
 *                   gap=xl — no Glow decoration; recovery is not a primary
 *                   conversion screen, per Figma)
 *       Row (Back button left-aligned)
 *       Stack gap=m, alignX=center  (TitleBlock)
 *         Row (icon + heading line)
 *         Description paragraph
 *         ChannelHint (surface-2 tinted info pill — local styled)
 *       Conditional:
 *         If success → Stack with confirmation message + ghost troubleshoot
 *         Else → Stack with SegmentedButton + Input + Submit
 *
 * No "Footer" under the card — original doesn't have one either.
 *
 * @format
 */

import {useEffect} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';
import {useTranslation} from 'react-i18next';
import {useForm, Controller} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';

import {SegmentedButton, Segment} from 'ui/components/molecules/SegmentedButton';
import {Input} from 'ui/components/atoms/Input';
import {Button} from 'ui/components/atoms/Button';
import {Icon} from 'ui/components/atoms/Icon';
import {Stack} from 'ui/components/layout/Stack';
import {Row} from 'ui/components/layout/Row';
import {CenteredCard} from 'ui/components/composition/CenteredCard';
import {text} from 'ui/theme/typography';

import {AuthShell} from '../_shell';
import {TroubleshootingModal} from './TroubleshootingModal';

export const RECOVERY_MODES = ['phone', 'mail'] as const;
export type RecoveryMode = (typeof RECOVERY_MODES)[number];

export interface PasswordRecoveryProps {
  /** Channel selected. Controlled. Default 'phone'. */
  mode?: RecoveryMode;
  /** Preseed the input. */
  initialValue?: string;
  /**
   * Opaque server error shown near the submit button (not tied to the field).
   * The backend returns machine codes we can't reliably attribute.
   */
  serverError?: string;
  /** 'idle' → form; 'success' → confirmation message + troubleshoot link. */
  sentState?: 'idle' | 'success';
  /** Request in flight — disables the submit button and shows a spinner. */
  submitState?: 'idle' | 'submitting';
  /** Показать модалку «Не приходит пароль» поверх страницы. */
  troubleshootingOpen?: boolean;
  onModeChange?: (next: RecoveryMode) => void;
  onSubmit?: (data: {mode: RecoveryMode; value: string}) => void;
  onBackClick?: () => void;
  /** Возврат на экран входа (primary-кнопка в success-состоянии). */
  onBackToSignInClick?: () => void;
  /** Триггер открытия модалки (ссылка «Не приходит пароль?» на success). */
  onTroubleshootingClick?: () => void;
  /** Закрыть модалку. */
  onTroubleshootingClose?: () => void;
  /** «Попробовать ещё» внутри модалки. */
  onTroubleshootingRetry?: () => void;
  /** «Связаться с поддержкой» внутри модалки. */
  onContactSupport?: () => void;
  onLogoClick?: () => void;
}

const FIELD_BY_MODE: Record<
  RecoveryMode,
  {type: 'tel' | 'email'; placeholder: string; icon: string; autoComplete: string}
> = {
  phone: {type: 'tel', placeholder: 'Телефон', icon: 'call', autoComplete: 'tel'},
  mail: {type: 'email', placeholder: 'Емейл', icon: 'mail', autoComplete: 'email'},
};

const SUCCESS_COPY: Record<RecoveryMode, {key: string; display: string}> = {
  phone: {
    key: 'Мы отправили пароль на ваш телефон. Проверьте SMS.',
    display: 'Мы отправили пароль на\u00A0ваш телефон. Проверьте\u00A0SMS.',
  },
  mail: {
    key: 'Мы отправили пароль на вашу почту. Проверьте письмо.',
    display: 'Мы отправили пароль на\u00A0вашу почту. Проверьте\u00A0письмо.',
  },
};

// Heading row uses the heading.l role with an inline icon — keeping it as
// one styled span lets us bind the typography token without the chrome
// the Row primitive would impose.
const HeadingLine = styled(Box)(({theme}) => ({
  ...text('heading.l'),
  color: theme.palette.surface.onSurface,
}));

const Description = styled('p')(({theme}) => ({
  ...text('body.l'),
  color: theme.palette.surface.onSurface,
  textAlign: 'center',
  margin: 0,
}));

// surface-2 tinted info pill. Custom because the NoticeCard primitive
// applies a tone-tinted fill — this one wants the plain second-tier
// surface (a neutral elevation, not a notice). One narrow styled element.
const ChannelHint = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  width: '100%',
  boxSizing: 'border-box',
  borderRadius: 8,
  backgroundColor: theme.palette.surface.surface2,
  color: theme.palette.surface.onSurface,
  ...text('body.s'),
}));

// General server-error line, centered above the submit button.
const ServerError = styled(Box)(({theme}) => ({
  ...text('body.s'),
  color: theme.palette.bad.main,
  textAlign: 'center',
  width: '100%',
}));

export function PasswordRecovery({
  mode = 'phone',
  initialValue,
  serverError,
  sentState = 'idle',
  submitState = 'idle',
  troubleshootingOpen = false,
  onModeChange,
  onSubmit,
  onBackClick,
  onBackToSignInClick,
  onTroubleshootingClick,
  onTroubleshootingClose,
  onTroubleshootingRetry,
  onContactSupport,
  onLogoClick,
}: PasswordRecoveryProps) {
  const {t} = useTranslation();

  // Email format is enforced only in mail mode (via the `$mode` context);
  // emptiness is handled by the button's disabled state.
  const schema = yup.object({
    value: yup.string().when('$mode', {
      is: 'mail',
      then: s => s.email(t('Введен некорректный email-адрес', 'Введен некорректный email-адрес')),
      otherwise: s => s,
    }),
  });

  const {control, handleSubmit, watch, clearErrors} = useForm({
    defaultValues: {value: initialValue ?? ''},
    resolver: yupResolver(schema),
    context: {mode},
    mode: 'onTouched',
  });

  // Switching phone↔mail drops any stale email-format error until the user
  // next interacts with the field.
  useEffect(() => {
    clearErrors('value');
  }, [mode, clearErrors]);

  const {value} = watch();
  const isSuccess = sentState === 'success';
  const isSubmitting = submitState === 'submitting';
  const isValid = Boolean(value) && !isSuccess && !isSubmitting;

  const onValid = (values: {value?: string}) => {
    onSubmit?.({mode, value: values.value ?? ''});
  };

  const fieldMeta = FIELD_BY_MODE[mode];

  return (
    <AuthShell onLogoClick={onLogoClick}>
      <CenteredCard
        maxWidthPx={420}
        radius='xl'
        paddingX='m'
        paddingTop='m'
        paddingBottom='xl'
        gap='xl'
        cardAs='form'
        cardProps={{onSubmit: handleSubmit(onValid), noValidate: true}}
      >
        <Row justify='start'>
          <Button
            type='button'
            variant='ghost'
            color='neutral'
            size='m'
            leftIcon='arrow_back'
            onClick={onBackClick}
          >
            {t('Назад', 'Назад')}
          </Button>
        </Row>

        <Stack gap='m' alignX='center'>
          <Row gap='s' alignY='center'>
            <Icon name='key' size='l' color='on-surface' />
            <HeadingLine>{t('Восстановление пароля', 'Восстановление пароля')}</HeadingLine>
          </Row>
          <Description>
            {t(
              'Введите номер или емейл своего аккаунта, и мы отправим на него пароль',
              'Введите номер или\u00A0емейл своего аккаунта, и\u00A0мы отправим на\u00A0него\u00A0пароль',
            )}
          </Description>
          <ChannelHint>
            <Icon name='mail_asterisk' size='l' weight={200} color='on-surface' />
            <Box component='span'>
              {t(
                'Восстановление по емейлу используется для сотрудников, добавленных из основного кабинета',
                'Восстановление по\u00A0емейлу используется для\u00A0сотрудников, добавленных из\u00A0основного\u00A0кабинета',
              )}
            </Box>
          </ChannelHint>
        </Stack>

        {isSuccess ? (
          <Stack gap='m' alignX='center'>
            <Description>{t(SUCCESS_COPY[mode].key, SUCCESS_COPY[mode].display)}</Description>
            <Button
              type='button'
              variant='solid'
              color='primary'
              size='m'
              fullWidth
              leftIcon='chevron_backward'
              onClick={onBackToSignInClick}
            >
              {t('На экран входа', 'На экран входа')}
            </Button>
            <Button
              type='button'
              variant='ghost'
              color='primary'
              size='s'
              onClick={onTroubleshootingClick}
            >
              {t('Не приходит пароль?', 'Не приходит пароль?')}
            </Button>
          </Stack>
        ) : (
          <Stack gap='m' alignX='center'>
            <SegmentedButton
              mode='radio'
              value={mode}
              onChange={next => onModeChange?.(next as RecoveryMode)}
              size='s'
              disabled={isSubmitting}
            >
              <Segment value='phone' label={t('Телефон', 'Телефон')} />
              <Segment value='mail' label={t('Емейл', 'Емейл')} />
            </SegmentedButton>
            <Controller
              name='value'
              control={control}
              render={({field, fieldState}) => (
                <Input
                  {...field}
                  size='m'
                  type={fieldMeta.type}
                  fullWidth
                  leftIcon={fieldMeta.icon}
                  placeholder={t(fieldMeta.placeholder, fieldMeta.placeholder)}
                  autoComplete={fieldMeta.autoComplete}
                  errorMessage={fieldState.error?.message}
                  disabled={isSubmitting}
                />
              )}
            />
            {serverError && <ServerError role='alert'>{serverError}</ServerError>}
            <Button
              type='submit'
              variant='solid'
              color='good'
              size='m'
              fullWidth
              disabled={!isValid}
              loading={isSubmitting ? 'left' : false}
            >
              {isSubmitting
                ? t('Отправляем пароль', 'Отправляем пароль')
                : t('Отправить пароль', 'Отправить пароль')}
            </Button>
          </Stack>
        )}
      </CenteredCard>

      <TroubleshootingModal
        open={troubleshootingOpen}
        onClose={() => onTroubleshootingClose?.()}
        onRetry={() => onTroubleshootingRetry?.()}
        onContactSupport={() => onContactSupport?.()}
      />
    </AuthShell>
  );
}
